import React, { useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSnackbar } from "notistack";

import "./SalaryActions.scss";
import Loader from "../loder";
import DateCarousel from "../date-carousel";
import YeulsButton from "../yeuls-button";
import ConfirmDialog from "../confirm-dialog";
import { QueueFileStatus } from "../../shared/constants";
import { mergeSalaries, syncFromMaskoreshet } from "../../api/requests/yeuls";
import useQueueFilePolling from "../../hooks/useQueueFilePolling";

const SalaryActions = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [showLoader, setShowLoader] = useState(false);
  const [showMergeSalariesConfirmDialog, setShowMergeSalariesConfirmDialog] = useState(false);
  const [showGeneratePdfConfirm, setShowGeneratePdfConfirm] = useState(false);
  const [syncFromMaskoreshetConfirmDialog, setShowSyncFromMaskoreshetConfirmDialog] = useState(false);
  const [currentMonth, setCurrentMonth] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [syncMaskoreshetDate, setSyncMaskoreshetDate] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const { queueFilesJob, isGeneratePdfLoading, availableSalariesCount, handleGenerateMonthlyPdfs } = useQueueFilePolling(currentMonth);

  const getButtonText = useMemo(() => {
    if (queueFilesJob?.status === QueueFileStatus.failed) {
      return t("restart-pdf-generation");
    }
    if (queueFilesJob?.status === QueueFileStatus.in_progress) {
      return t("generating-pdf");
    }
    if (queueFilesJob?.status === QueueFileStatus.done) {
      if (availableSalariesCount > 0) {
        return t("generate-pdf");
      }
      return t("pdf-generated");
    }
    return t("generate-pdf");
  }, [queueFilesJob, availableSalariesCount, t]);

  const isButtonDisabled = useMemo(() => {
    if (isGeneratePdfLoading || queueFilesJob?.status === QueueFileStatus.in_progress) {
      return true;
    }

    if (queueFilesJob?.status === QueueFileStatus.failed && availableSalariesCount !== 0) {
      return false;
    }

    return availableSalariesCount === 0;
  }, [isGeneratePdfLoading, queueFilesJob, availableSalariesCount]);

  return (
    <div className="salary-actions-page">
      {showLoader && <Loader />}
      <h1 className="salary-actions-title">{t("salary-actions")}</h1>
      <div className="salary-actions-month">
        <DateCarousel onMonthChange={setCurrentMonth} />
      </div>
      <div className="salary-actions-buttons">
        <YeulsButton
          onClick={() => setShowMergeSalariesConfirmDialog(true)}
          variant="outlined"
          className="sync-button"
        >
          {t("sync-salaries")}
        </YeulsButton>
        <YeulsButton
          onClick={() => setShowGeneratePdfConfirm(true)}
          disabled={isButtonDisabled}
          variant={queueFilesJob?.status === QueueFileStatus.failed ? "outlined" : "contained"}
        >
          {getButtonText}
        </YeulsButton>
        <YeulsButton
          onClick={() => {
            setSyncMaskoreshetDate(currentMonth);
            setShowSyncFromMaskoreshetConfirmDialog(true);
          }}
          variant="outlined"
          className="sync-button"
        >
          {t("sync-from-maskoreshet")}
        </YeulsButton>
      </div>

      <ConfirmDialog
        open={showMergeSalariesConfirmDialog}
        onClose={() => setShowMergeSalariesConfirmDialog(false)}
        title={t("sync-salaries")}
        onSubmit={() => {
          setShowLoader(true);
          setShowMergeSalariesConfirmDialog(false);
          mergeSalaries()
            .then(() => {
              enqueueSnackbar(t("salaries-merged-successfully"), { variant: "success" });
            })
            .catch(() => {
              enqueueSnackbar(t("something-went-wrong"), { variant: "error" });
            })
            .finally(() => {
              setShowLoader(false);
            });
        }}
      >
        <div className="dialog-content">{t("sure-sync")}</div>
      </ConfirmDialog>

      <ConfirmDialog
        open={syncFromMaskoreshetConfirmDialog}
        onClose={() => setShowSyncFromMaskoreshetConfirmDialog(false)}
        title={t("sync-from-maskoreshet")}
        onSubmit={() => {
          setShowLoader(true);
          setShowSyncFromMaskoreshetConfirmDialog(false);
          syncFromMaskoreshet(syncMaskoreshetDate.month, syncMaskoreshetDate.year)
            .then(() => {
              enqueueSnackbar(t("data-synced-from-maskoreshet"), { variant: "success" });
            })
            .catch(() => {
              enqueueSnackbar(t("something-went-wrong"), { variant: "error" });
            })
            .finally(() => {
              setShowLoader(false);
            });
        }}
      >
        <div className="dialog-content salary-actions-month-fields">
          <div>
            <label>{t("month")}</label>
            <input
              type="number"
              min="1"
              max="12"
              value={syncMaskoreshetDate.month}
              onChange={(e) => {
                const month = Number(e.target.value);
                setSyncMaskoreshetDate((prev) => ({ ...prev, month }));
              }}
            />
          </div>
          <div>
            <label>{t("year")}</label>
            <input
              type="number"
              min="2000"
              max="2100"
              value={syncMaskoreshetDate.year}
              onChange={(e) => {
                const year = Number(e.target.value);
                setSyncMaskoreshetDate((prev) => ({ ...prev, year }));
              }}
            />
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={showGeneratePdfConfirm}
        onClose={() => setShowGeneratePdfConfirm(false)}
        title={
          <div className="salary-actions-pdf-title">
            <div>
              <Trans
                i18nKey="confirm-generate-pdf-title"
                values={{
                  month: currentMonth.month,
                  year: currentMonth.year,
                }}
              />
            </div>
            <div>
              <Trans
                i18nKey="confirm-generate-pdf-subtitle"
                values={{
                  count: availableSalariesCount ?? "",
                }}
                components={{
                  count: <span className="salary-actions-pdf-count" />,
                }}
              />
            </div>
          </div>
        }
        onSubmit={async () => {
          setShowGeneratePdfConfirm(false);
          await handleGenerateMonthlyPdfs();
        }}
      />
    </div>
  );
};

export default SalaryActions;
